import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Check, X } from "lucide-react";

// Reference product library — rates and terms are illustrative averages,
// not offers. Copy at the bottom of the section makes this explicit.
const PRODUCTS = [
  { id: "sba",       name: "SBA 7(a)",              apr: 12.5, months: 120, min: 50000,   max: 5000000, kind: "loan" },
  { id: "sba504",    name: "SBA 504 (real estate)", apr: 7.0,  months: 300, min: 250000,  max: 5500000, kind: "loan" },
  { id: "term",      name: "Bank Term Loan",        apr: 13.0, months: 60,  min: 25000,   max: 2000000, kind: "loan" },
  { id: "line",      name: "Line of Credit",        apr: 15.0, months: 24,  min: 10000,   max: 500000,  kind: "loan" },
  { id: "equipment", name: "Equipment Financing",   apr: 11.0, months: 60,  min: 25000,   max: 2000000, kind: "loan" },
  { id: "revenue",   name: "Revenue Financing",     factor: 1.35, months: 12, min: 25000, max: 2000000, kind: "revenue" },
  { id: "mca",       name: "Merchant Cash Advance", factor: 1.42, months: 8,  min: 5000,  max: 500000,  kind: "revenue" },
];

const money = (n) => {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1000)}K`;
  return `$${n}`;
};

const pmt = (principal, aprPct, months) => {
  const r = aprPct / 100 / 12;
  if (r === 0) return principal / months;
  return (principal * r) / (1 - Math.pow(1 + r, -months));
};

const estimateForProduct = (p, amount) => {
  const eligible = amount >= p.min && amount <= p.max;
  if (p.kind === "loan") {
    const monthly = pmt(amount, p.apr, p.months);
    return {
      eligible,
      monthly,
      detail: `${p.apr.toFixed(1)}% APR · ${Math.round(p.months / 12)}-yr term`,
    };
  }
  // Revenue / MCA — factor-rate payback split across months
  const payback = amount * p.factor;
  const monthly = payback / p.months;
  return {
    eligible,
    monthly,
    detail: `${p.factor}× factor · ${p.months}-month payback`,
  };
};

const STEPS = [10000, 25000, 50000, 100000, 250000, 500000, 1000000, 2500000, 5000000];

export const FundingEstimator = () => {
  const [idx, setIdx] = useState(3); // default $100K
  const amount = STEPS[idx];

  const rows = useMemo(
    () => PRODUCTS.map((p) => ({ product: p, ...estimateForProduct(p, amount) })),
    [amount]
  );

  return (
    <section
      id="estimator"
      className="py-32 md:py-44 border-t border-black/10"
      style={{ background: "linear-gradient(180deg, rgba(196,164,124,0.04), transparent 60%)" }}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-8 items-end mb-12">
          <div className="col-span-12 md:col-span-7">
            <div className="overline">Funding estimator · 60-second read</div>
            <h2 className="mt-4 font-serif text-5xl md:text-6xl leading-[0.95]">
              How much do you <em className="italic text-[color:var(--gold)]">need</em>?
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5">
            <p className="text-[color:var(--bone)]/75 leading-relaxed">
              Move the slider. We'll show the products your business likely qualifies for at that amount, with a rough monthly payment for each. Estimates only — actual offers depend on credit, revenue, and time in business.
            </p>
          </div>
        </div>

        {/* Slider block */}
        <div className="border border-black/10 bg-[color:var(--bg-elev)] p-8 md:p-12">
          <div className="flex items-end justify-between mb-4">
            <div>
              <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/50">
                Funding amount
              </div>
              <div className="mt-2 font-serif text-6xl md:text-7xl leading-none tracking-tight text-[color:var(--gold)]">
                {money(amount)}
              </div>
            </div>
            <Link
              to="/apply"
              className="hidden md:inline-flex items-center gap-3 bg-[color:var(--gold)] text-black px-6 py-3 font-mono text-xs tracking-[0.24em] uppercase hover:bg-[color:var(--gold-strong)] transition-colors"
            >
              Get real offers <ArrowRight size={14} />
            </Link>
          </div>

          <input
            data-testid="estimator-slider"
            type="range"
            min={0}
            max={STEPS.length - 1}
            value={idx}
            onChange={(e) => setIdx(Number(e.target.value))}
            className="w-full mt-6 estimator-slider"
            style={{ "--val": `${(idx / (STEPS.length - 1)) * 100}%` }}
            aria-label="Funding amount"
          />

          <div className="mt-3 flex justify-between font-mono text-[10px] tracking-[0.22em] uppercase text-[color:var(--bone)]/45">
            {STEPS.map((s, i) => (
              <span key={s} className={i === idx ? "text-[color:var(--gold)]" : ""}>
                {money(s)}
              </span>
            ))}
          </div>

          {/* Products table */}
          <div className="mt-12 overflow-x-auto">
            <table data-testid="estimator-table" className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-black/10 font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/50">
                  <th className="py-4 pr-6">Product</th>
                  <th className="py-4 pr-6">Terms</th>
                  <th className="py-4 pr-6 text-right">Est. monthly</th>
                  <th className="py-4 pr-6">Qualification</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ product, eligible, monthly, detail }) => (
                  <motion.tr
                    key={product.id}
                    initial={false}
                    animate={{
                      opacity: eligible ? 1 : 0.36,
                    }}
                    transition={{ duration: 0.4 }}
                    className="border-b border-black/[0.06]"
                  >
                    <td className="py-5 pr-6">
                      <div className="font-serif text-2xl md:text-3xl leading-tight text-[color:var(--bone)]">
                        {product.name}
                      </div>
                    </td>
                    <td className="py-5 pr-6 font-mono text-xs tracking-wide text-[color:var(--bone)]/70">
                      {detail}
                    </td>
                    <td className="py-5 pr-6 text-right">
                      <div className={`font-serif text-2xl md:text-3xl ${eligible ? "text-[color:var(--gold)]" : "text-[color:var(--bone)]/50"}`}>
                        ${Math.round(monthly).toLocaleString()}
                        <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/40 ml-2">
                          /mo
                        </span>
                      </div>
                    </td>
                    <td className="py-5 pr-6">
                      {eligible ? (
                        <span className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--gold)]">
                          <Check size={12} /> Likely fit
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/45">
                          <X size={12} />
                          {amount < product.min ? "Below product min" : "Above product cap"}
                        </span>
                      )}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-10 font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/45 max-w-2xl">
            Illustrative only. Actual offers depend on credit, revenue, time in business, and industry. Move to Apply for real quotes from real lenders.
          </p>
        </div>
      </div>
    </section>
  );
};

export default FundingEstimator;
